import {
  activeBackend,
  allowsRuntimeBackendSwitch
} from '~/configs/env'
import type { OrgPath } from '~/data/types/organization'

const STORAGE_KEY_BASE = 'synop.cloakedOrgPath'

/**
 * Preview builds share one origin across gamma / prod / prod_eu, so the restore
 * key must be backend-scoped. Deployed profiles never switch backends at runtime
 * and keep the unscoped key (separate origins already isolate storage).
 */
function storageKey(): string {
  return allowsRuntimeBackendSwitch ? `${STORAGE_KEY_BASE}.${activeBackend}` : STORAGE_KEY_BASE
}

function readRaw(key: string): OrgPath | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(key)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed) || parsed.length === 0) return null
    const path: OrgPath = []
    for (const entry of parsed) {
      if (
        typeof entry !== 'object' ||
        entry === null ||
        typeof (entry as { id?: unknown }).id !== 'string' ||
        typeof (entry as { name?: unknown }).name !== 'string'
      ) {
        return null
      }
      path.push({ id: (entry as { id: string }).id, name: (entry as { name: string }).name })
    }
    return path
  } catch {
    return null
  }
}

function removeKey(key: string) {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(key)
  } catch {
    // ignore
  }
}

export function readCloakedOrgPath(): OrgPath | null {
  const scoped = readRaw(storageKey())
  if (scoped) return scoped

  // Preview used to write an unscoped key. That value is ambiguous across
  // backends on the same origin — never restore it; drop it so it cannot leak
  // a prod US cloak into prod EU (or vice versa).
  if (allowsRuntimeBackendSwitch) {
    removeKey(STORAGE_KEY_BASE)
  }
  return null
}

export function writeCloakedOrgPath(path: OrgPath) {
  if (typeof window === 'undefined') return
  try {
    const key = storageKey()
    if (path.length <= 1) {
      window.sessionStorage.removeItem(key)
      return
    }
    window.sessionStorage.setItem(key, JSON.stringify(path))
  } catch {
    // sessionStorage may be unavailable (private mode / quota); cloaking still works in-memory.
  }
}

export function clearCloakedOrgPath() {
  removeKey(storageKey())
  if (allowsRuntimeBackendSwitch) {
    removeKey(STORAGE_KEY_BASE)
  }
}
